import { Children, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { FLOATING_THUMB_CLASS } from "@tellescope/react-hooks";
import { cn } from "../../lib/utils";
import { PaginatedListEmpty } from "./PaginatedListEmpty";
import { PaginatedListLoading } from "./PaginatedListLoading";
import { PaginatedListViewport } from "./PaginatedListViewport";
import type { PaginatedListProps } from "./types";
import { usePaginatedList } from "./usePaginatedList";

/**
 * A virtualized scroll container that reports when its start or end is reached, for infinite
 * pagination. Renders only the visible window of `children` via `virtua`.
 *
 * Reaching an edge fires the matching callback once, re-arming only after the list leaves that edge.
 * A list too short to scroll counts as having reached its end, so a first page that underfills the
 * viewport still paginates — gated on `isLoading` so an unsettled list never does.
 *
 * `isLoading` renders a loading state and `emptyMessage` an empty one, each in place of the list;
 * loading wins when both apply.
 *
 * @example
 * ```tsx
 * <PaginatedList isLoading={query.isLoading} onBottomReached={query.fetchNextPage} className="h-96">
 *   {items.map((item) => (
 *     <Item key={item.id} {...item} />
 *   ))}
 * </PaginatedList>
 * ```
 *
 * @example Inside an ancestor that already scrolls, and with a floating scrollbar.
 * ```tsx
 * <PaginatedList scrollOptions={{ element: scrollElement, measureStartMargin: true }}>
 * <PaginatedList scrollOptions={{ type: "floating", fadeWhenClipped: true }}>
 * ```
 *
 * @example A chat thread: opens at the newest message and pages older ones in above.
 * ```tsx
 * <PaginatedList anchor="end" onTopReached={query.fetchNextPage} emptyMessage={t("No messages yet")}>
 *   {messages.map((message) => (
 *     <MessageBubble key={message.id} {...message} />
 *   ))}
 * </PaginatedList>
 * ```
 */
export const PaginatedList = ({
  children,
  className,
  onBottomReached,
  onTopReached,
  threshold = 50,
  isLoading = false,
  loadingMessage,
  label,
  scrollOptions,
  resetKey,
  anchor = "start",
  emptyMessage,
  onFocusWithin,
  onClickWithin,
  viewportRef,
  contentMinWidth,
  role,
  ref,
}: PaginatedListProps) => {
  const { t } = useTranslation();
  const {
    virtualizerRef,
    attachScroller,
    thumbRef,
    handleScroll,
    externalScrollRef,
    startMargin,
    ownsScrolling,
    showFloatingThumb,
    scrollbar,
    isScrolling,
    surfaceStyle,
    handleFocusCapture,
    handleClickCapture,
    shift,
    isAnchoredToEnd,
    scrollToEnd,
  } = usePaginatedList({
    onBottomReached,
    onTopReached,
    onFocusWithin,
    onClickWithin,
    threshold,
    isLoading,
    scrollOptions,
    anchor,
    children,
  });

  useImperativeHandle(ref, () => ({ scrollToEnd }), [scrollToEnd]);

  // Memoized for the same reason `attachScroller` is: the viewport's resize observer tracks element
  // identity, so an inline callback ref would detach and re-attach the node on every render.
  const attachViewport = useCallback(
    (element: HTMLDivElement | null) => {
      attachScroller(element);
      if (typeof viewportRef === "function") viewportRef(element);
      else if (viewportRef) viewportRef.current = element;
    },
    [attachScroller, viewportRef]
  );

  if (isLoading) return <PaginatedListLoading className={className} message={loadingMessage} />;

  // Below the `isLoading` return above, so a list that has not settled shows the loading state
  // rather than briefly claiming to be empty. `Children.count` skips `null` and `false` entries, so
  // a list whose children are all conditioned out reads as empty rather than as one blank row.
  if (emptyMessage !== undefined && Children.count(children) === 0) {
    return <PaginatedListEmpty className={className} message={emptyMessage} />;
  }

  // Shared so the two branches differ only in where `className` lands, rather than in a dozen props
  // each — the kind of duplication that lets a behavior work under one scrollbar type and not another.
  const viewportProps = {
    label: label ?? t("List"),
    virtualizerRef,
    scrollerRef: attachViewport,
    onScroll: handleScroll,
    externalScrollRef,
    startMargin,
    ownsScrolling,
    scrollbar,
    isScrolling,
    style: surfaceStyle,
    resetKey,
    shift,
    onFocusCapture: handleFocusCapture,
    onClickCapture: handleClickCapture,
    isAnchoredToEnd,
    contentMinWidth,
    role,
  };

  // The floating thumb overlays the scroller, so it needs a positioned wrapper to sit in. The
  // consumer's `className` moves to that wrapper and the scroller fills it, keeping any height or
  // flex sizing applied to the outermost element either way.
  if (showFloatingThumb) {
    return (
      <div data-slot="paginated-list" className={cn("relative", className)}>
        <PaginatedListViewport {...viewportProps} className="h-full">
          {children}
        </PaginatedListViewport>
        <div ref={thumbRef} className={FLOATING_THUMB_CLASS} />
      </div>
    );
  }

  return (
    <PaginatedListViewport {...viewportProps} className={className}>
      {children}
    </PaginatedListViewport>
  );
};
